import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban, Users, UserPlus, X, ChevronRight,
  Calendar, MapPin, CheckCircle, Building2, Link2, Unlink,
  Square, CheckSquare, Send, Clock, AlertCircle, Search, UserMinus,
  FileCheck, FileX, ShieldCheck, ShieldAlert, ChevronUp, ChevronDown,
  Globe, RefreshCw, Award, Wind
} from 'lucide-react';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Project Card Component
const ProjectCard = ({ project, isSelected, onClick }) => {
  const getStatusStyle = (status) => {
    const styles = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      active: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      archived: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
    };
    return styles[status] || styles.draft;
  };

  const style = getStatusStyle(project.status);

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-xl cursor-pointer transition-all border ${
        isSelected
          ? 'bg-orange-50 border-orange-200 shadow-md'
          : 'bg-slate-50 border-slate-100 hover:bg-slate-100 hover:border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
            isSelected ? 'bg-orange-200' : 'bg-orange-100'
          }`}>
            <Globe className="h-4.5 w-4.5 text-orange-500" />
          </div>
          <div>
            <span className="text-[11px] font-black text-orange-500 uppercase tracking-wide">{project.code}</span>
            <h3 className={`text-base font-black ${isSelected ? 'text-orange-700' : 'text-slate-900'}`}>{project.name}</h3>
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 ${isSelected ? 'text-orange-500' : 'text-slate-300'}`} />
      </div>

      <div className="flex items-center gap-2 mt-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-black rounded-md uppercase ${style.bg} ${style.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {project.status}
        </span>
        {project.location && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500 font-medium">
            <MapPin className="h-3.5 w-3.5" />
            {project.location}
          </span>
        )}
      </div>
    </div>
  );
};

const WindCompanyProjects = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignProject, setAssignProject] = useState(null);
  const [availableAuthorizations, setAvailableAuthorizations] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  // Project Mapping state
  const [projectMappings, setProjectMappings] = useState({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [availableWindProjects, setAvailableWindProjects] = useState([]);
  const [loadingWindProjects, setLoadingWindProjects] = useState(false);
  const [mappingInProgress, setMappingInProgress] = useState(false);

  // Planned technicians from Resource Board
  const [plannedTechnicians, setPlannedTechnicians] = useState([]);
  const [loadingPlannedTechnicians, setLoadingPlannedTechnicians] = useState(false);

  // Bulk nomination selection
  const [selectedForNomination, setSelectedForNomination] = useState(new Set());
  const [showBulkNominationModal, setShowBulkNominationModal] = useState(false);
  const [bulkNominationLoading, setBulkNominationLoading] = useState(false);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [bulkNominationForm, setBulkNominationForm] = useState({
    authorization_level: '',
    date_start: '',
    date_end: ''
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Unassign state
  const [unassigningId, setUnassigningId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const projectsResponse = await api.get('/oem/projects/');
        setProjects(projectsResponse.data);

        const assignmentsResponse = await api.get('/oem/project-assignments/');
        setProjectAssignments(assignmentsResponse.data);

        // Fetch project mappings for all projects
        try {
          const mappingsResponse = await api.get('/oem/project-mappings/my-mappings/');
          const mappingsData = mappingsResponse.data || [];
          const mappingsMap = {};
          mappingsData.forEach(m => {
            mappingsMap[m.oem_project] = m;
          });
          setProjectMappings(mappingsMap);
        } catch (mappingErr) {
          console.log('No mappings found:', mappingErr);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const fetchAvailableForProject = async (project) => {
    setAssignProject(project);
    setShowAssignModal(true);
    setLoadingAvailable(true);
    try {
      const response = await api.get(`/oem/projects/${project.id}/available-authorizations/`);
      setAvailableAuthorizations(response.data.authorizations || []);
    } catch (err) {
      console.error('Error fetching available authorizations:', err);
      setAvailableAuthorizations([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleAssignToProject = async (authorization) => {
    if (!assignProject || assigningId) return;

    setAssigningId(authorization.id);
    try {
      await api.post(`/oem/projects/${assignProject.id}/assignments/`, {
        authorization: authorization.id
      });

      setAvailableAuthorizations(prev =>
        prev.filter(a => a.id !== authorization.id)
      );

      const assignmentsResponse = await api.get('/oem/project-assignments/');
      setProjectAssignments(assignmentsResponse.data);

      setProjects(prev => prev.map(p =>
        p.id === assignProject.id
          ? { ...p, technicians_count: (p.technicians_count || 0) + 1 }
          : p
      ));
    } catch (err) {
      console.error('Error assigning technician:', err);
      alert(err.response?.data?.error || err.response?.data?.detail || 'Failed to assign technician');
    } finally {
      setAssigningId(null);
    }
  };

  const handleUnassignFromProject = async (assignment) => {
    if (unassigningId) return;

    if (!window.confirm(`Are you sure you want to unassign ${assignment.user_name} from this project?`)) {
      return;
    }

    setUnassigningId(assignment.id);
    try {
      await api.delete(`/oem/project-assignments/${assignment.id}/`);

      setProjectAssignments(prev => prev.filter(a => a.id !== assignment.id));

      if (selectedProject) {
        setProjects(prev => prev.map(p =>
          p.id === selectedProject.id
            ? { ...p, technicians_count: Math.max((p.technicians_count || 1) - 1, 0) }
            : p
        ));
      }
    } catch (err) {
      console.error('Error unassigning technician:', err);
      alert(err.response?.data?.error || err.response?.data?.detail || 'Failed to unassign technician');
    } finally {
      setUnassigningId(null);
    }
  };

  const selectedProjectTechnicians = selectedProject
    ? projectAssignments.filter(a => a.project === selectedProject.id)
    : [];

  const currentMapping = selectedProject ? projectMappings[selectedProject.id] : null;

  useEffect(() => {
    const fetchPlannedTechnicians = async () => {
      if (!currentMapping?.id) {
        setPlannedTechnicians([]);
        return;
      }

      setLoadingPlannedTechnicians(true);
      try {
        const response = await api.get(`/oem/project-mappings/${currentMapping.id}/planned-technicians/`);
        setPlannedTechnicians(response.data?.technicians || []);
      } catch (err) {
        console.error('Error fetching planned technicians:', err);
        setPlannedTechnicians([]);
      } finally {
        setLoadingPlannedTechnicians(false);
      }
    };

    fetchPlannedTechnicians();
  }, [currentMapping?.id]);

  useEffect(() => {
    setSearchTerm('');
    setSelectedRoleFilter('');
    setSelectedStatusFilter('');
    setSelectedForNomination(new Set());
    setSortBy(null);
    setSortOrder('asc');
  }, [selectedProject?.id]);

  const availableRoles = [...new Set(
    plannedTechnicians.flatMap(tech => tech.global_roles || [])
  )].sort();

  const openMappingModal = async () => {
    if (!selectedProject) return;
    setShowMappingModal(true);
    setLoadingWindProjects(true);
    try {
      const response = await api.get(`/oem/project-mappings/available-wind-projects/?oem_project=${selectedProject.id}`);
      setAvailableWindProjects(response.data || []);
    } catch (err) {
      console.error('Error fetching wind projects:', err);
      setAvailableWindProjects([]);
    } finally {
      setLoadingWindProjects(false);
    }
  };

  const handleCreateMapping = async (windProject) => {
    if (!selectedProject || mappingInProgress) return;
    setMappingInProgress(true);
    try {
      const response = await api.post('/oem/project-mappings/', {
        oem_project: selectedProject.id,
        wind_project: windProject.id
      });
      setProjectMappings(prev => ({
        ...prev,
        [selectedProject.id]: response.data
      }));
      setShowMappingModal(false);
    } catch (err) {
      console.error('Error creating mapping:', err);
      alert(err.response?.data?.detail || err.response?.data?.error || 'Failed to create mapping');
    } finally {
      setMappingInProgress(false);
    }
  };

  const handleRemoveMapping = async () => {
    if (!currentMapping) return;
    if (!window.confirm('Are you sure you want to remove the project mapping? All your technicians with OEM authorizations will become visible again.')) {
      return;
    }
    try {
      await api.delete(`/oem/project-mappings/${currentMapping.id}/`);
      setProjectMappings(prev => {
        const updated = { ...prev };
        delete updated[selectedProject.id];
        return updated;
      });
    } catch (err) {
      console.error('Error removing mapping:', err);
      alert(err.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <ChevronUp className="h-3 w-3 text-slate-300" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="h-3 w-3 text-orange-500" />
      : <ChevronDown className="h-3 w-3 text-orange-500" />;
  };

  const filteredPlannedTechnicians = plannedTechnicians
    .filter(tech => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const nameMatch = tech.name?.toLowerCase().includes(term);
        const emailMatch = tech.email?.toLowerCase().includes(term);
        if (!nameMatch && !emailMatch) return false;
      }

      if (selectedRoleFilter) {
        if (!tech.global_roles || !tech.global_roles.includes(selectedRoleFilter)) {
          return false;
        }
      }

      if (selectedStatusFilter) {
        if (selectedStatusFilter === 'authorized' && !tech.has_oem_authorization) return false;
        if (selectedStatusFilter === 'pending' && !tech.has_pending_nomination) return false;
        if (selectedStatusFilter === 'no_auth' && (tech.has_oem_authorization || tech.has_pending_nomination)) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (!sortBy) return 0;

      let comparison = 0;

      if (sortBy === 'roles') {
        const aRole = a.global_roles?.[0] || '';
        const bRole = b.global_roles?.[0] || '';
        if (!aRole && bRole) return 1;
        if (aRole && !bRole) return -1;
        comparison = aRole.localeCompare(bRole);
      } else if (sortBy === 'authorization') {
        const getAuthPriority = (tech) => {
          if (tech.has_oem_authorization) return 0;
          if (tech.has_pending_nomination) return 1;
          return 2;
        };
        const aPriority = getAuthPriority(a);
        const bPriority = getAuthPriority(b);
        if (aPriority !== bPriority) {
          comparison = aPriority - bPriority;
        } else if (a.has_oem_authorization && b.has_oem_authorization) {
          comparison = (a.authorization_level_code || '').localeCompare(b.authorization_level_code || '');
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const techsEligibleForNomination = filteredPlannedTechnicians.filter(
    tech => !tech.has_oem_authorization && !tech.has_pending_nomination
  );

  const toggleTechnicianSelection = (techId) => {
    setSelectedForNomination(prev => {
      const newSet = new Set(prev);
      if (newSet.has(techId)) {
        newSet.delete(techId);
      } else {
        newSet.add(techId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedForNomination.size === techsEligibleForNomination.length) {
      setSelectedForNomination(new Set());
    } else {
      setSelectedForNomination(new Set(techsEligibleForNomination.map(t => t.id)));
    }
  };

  const openBulkNominationModal = async () => {
    if (selectedForNomination.size === 0) return;
    setShowBulkNominationModal(true);

    try {
      const response = await api.get('/oem/authorization-levels/');
      setAvailableLevels(response.data || []);
    } catch (err) {
      console.error('Error fetching authorization levels:', err);
    }
  };

  const handleBulkNominationSubmit = async () => {
    if (!bulkNominationForm.authorization_level || !bulkNominationForm.date_start || !bulkNominationForm.date_end) {
      alert('Please fill in all fields');
      return;
    }

    setBulkNominationLoading(true);
    const selectedTechs = plannedTechnicians.filter(t => selectedForNomination.has(t.id));
    const errors = [];
    const successes = [];

    for (const tech of selectedTechs) {
      try {
        await api.post('/oem/nominations/', {
          user: tech.id,
          authorization_level: bulkNominationForm.authorization_level,
          date_start: bulkNominationForm.date_start,
          date_end: bulkNominationForm.date_end
        });
        successes.push(tech.name);
      } catch (err) {
        errors.push(`${tech.name}: ${err.response?.data?.detail || err.response?.data?.error || 'Failed'}`);
      }
    }

    setBulkNominationLoading(false);

    if (successes.length > 0) {
      alert(`Successfully created ${successes.length} nomination(s).\n${errors.length > 0 ? `\nErrors:\n${errors.join('\n')}` : ''}`);
      setShowBulkNominationModal(false);
      setSelectedForNomination(new Set());
      setBulkNominationForm({ authorization_level: '', date_start: '', date_end: '' });

      if (currentMapping?.id) {
        try {
          const response = await api.get(`/oem/project-mappings/${currentMapping.id}/planned-technicians/`);
          setPlannedTechnicians(response.data?.technicians || []);
        } catch (err) {
          console.error('Error refreshing technicians:', err);
        }
      }
    } else if (errors.length > 0) {
      alert(`Failed to create nominations:\n${errors.join('\n')}`);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading projects...</span>
          </div>
        </div>
      </div>
    );
  }

  const AssignModal = () => {
    if (!showAssignModal || !assignProject) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={() => setShowAssignModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                <UserPlus className="h-4 w-4 text-orange-500" />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Assign Technicians</h3>
                <p className="text-[10px] text-slate-500 font-medium">{assignProject.code} - {assignProject.name}</p>
              </div>
            </div>
            <button
              onClick={() => setShowAssignModal(false)}
              className="w-8 h-8 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {loadingAvailable ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-slate-600 text-sm">Loading...</span>
              </div>
            ) : availableAuthorizations.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-sm font-black text-slate-900 mb-1">No available technicians</p>
                <p className="text-xs text-slate-500 mb-4">All authorized technicians have been assigned.</p>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    navigate('/oem-company/nominations/create');
                  }}
                  className="text-[10px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-wide"
                >
                  Create a nomination →
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {availableAuthorizations.map((auth) => (
                  <div
                    key={auth.id}
                    className="p-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {auth.profile_picture ? (
                          <img
                            src={getImageUrl(auth.profile_picture)}
                            alt={auth.user_name}
                            className="w-10 h-10 rounded-xl object-cover border-2 border-orange-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-sm">
                            {auth.user_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-black text-slate-900">{auth.user_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className="inline-block px-2 py-0.5 text-[9px] font-black rounded-lg text-white uppercase"
                              style={{ backgroundColor: auth.authorization_level_color || '#F97316' }}
                            >
                              {auth.authorization_level_code}
                            </span>
                            <span className="text-[10px] text-slate-500 font-medium">
                              → {formatDate(auth.valid_until)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssignToProject(auth)}
                        disabled={assigningId === auth.id}
                        className="h-8 px-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600
                                 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide transition-colors"
                      >
                        {assigningId === auth.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-3.5 w-3.5" />
                            Assign
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-slate-100 flex justify-end bg-slate-50">
            <button
              onClick={() => setShowAssignModal(false)}
              className="h-9 px-4 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 font-black text-[10px] uppercase tracking-wide transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const MappingModal = () => {
    if (!showMappingModal || !selectedProject) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={() => setShowMappingModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                <Link2 className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Link Resource Board</h3>
                <p className="text-[10px] text-slate-500 font-medium">Pair with {selectedProject.code}</p>
              </div>
            </div>
            <button
              onClick={() => setShowMappingModal(false)}
              className="w-8 h-8 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>

          <div className="p-4 max-h-[60vh] overflow-y-auto">
            {loadingWindProjects ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-slate-600 text-sm">Loading projects...</span>
              </div>
            ) : availableWindProjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FolderKanban className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-sm font-black text-slate-900 mb-1">No projects available</p>
                <p className="text-xs text-slate-500">No projects in Resource Board to link.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide mb-3">
                  Only planned technicians will be visible
                </p>
                {availableWindProjects.map((proj) => (
                  <div
                    key={proj.id}
                    className="p-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-violet-50 hover:border-violet-200 transition-all cursor-pointer"
                    onClick={() => handleCreateMapping(proj)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                          <FolderKanban className="h-5 w-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900">{proj.name}</p>
                          <p className="text-[10px] text-slate-500 font-medium">{proj.code || `ID: ${proj.id}`}</p>
                        </div>
                      </div>
                      <button
                        disabled={mappingInProgress}
                        className="h-8 px-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700
                                 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide transition-colors"
                      >
                        {mappingInProgress ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Link2 className="h-3.5 w-3.5" />
                            Link
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-slate-100 flex justify-end bg-slate-50">
            <button
              onClick={() => setShowMappingModal(false)}
              className="h-9 px-4 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 font-black text-[10px] uppercase tracking-wide transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SuspendedCompanyOverlay>
      <div className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Header Card */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-orange-500" />
                </div>
                <div>
                  <h1 className="text-base font-black text-slate-900 uppercase tracking-tight">OEM Projects</h1>
                  <p className="text-[10px] text-slate-500 font-medium">Manage technicians in your projects</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/oem-company')}
                className="h-9 px-4 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          {projects.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FolderKanban className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-tight">No projects available</h3>
              <p className="text-xs text-slate-500">Contact your OEM Master administrator to be added to a project.</p>
            </div>
          ) : (
            <div className="p-5">
              {/* Projects Grid */}
              <div className="grid grid-cols-3 gap-3">
                {projects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      isSelected={selectedProject?.id === project.id}
                      onClick={() => setSelectedProject(project)}
                    />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Selected Project Details */}
        {selectedProject && (
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                    <FolderKanban className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">{selectedProject.code}</h2>
                      <span className="px-2 py-0.5 text-[9px] font-black rounded-md bg-emerald-100 text-emerald-600 uppercase">
                        {selectedProject.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">{selectedProject.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentMapping ? (
                    <button
                      onClick={handleRemoveMapping}
                      className="h-9 px-3 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide transition-colors"
                    >
                      <Unlink className="h-3.5 w-3.5" />
                      Unlink
                    </button>
                  ) : (
                    <button
                      onClick={openMappingModal}
                      className="h-9 px-3 bg-violet-100 text-violet-600 border border-violet-200 rounded-xl hover:bg-violet-200 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide transition-colors"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      Link Project
                    </button>
                  )}
                  <button
                    onClick={() => fetchAvailableForProject(selectedProject)}
                    className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors shadow-lg shadow-orange-500/20"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Assign Technicians
                  </button>
                </div>
              </div>

              {/* Project Info */}
              {selectedProject.location && (
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {selectedProject.location}
                  </span>
                </div>
              )}
            </div>

            {/* Technicians Section Header */}
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Users className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">Technicians</h3>
                    <p className="text-[10px] text-slate-500 font-medium">
                      {currentMapping
                        ? `Planned: ${plannedTechnicians.length}${searchTerm ? ` (showing ${filteredPlannedTechnicians.length})` : ''} | Assigned: ${selectedProjectTechnicians.length}`
                        : `Assigned: ${selectedProjectTechnicians.length}`
                      }
                    </p>
                  </div>
                </div>
                {currentMapping && techsEligibleForNomination.length > 0 && (
                  <button
                    onClick={openBulkNominationModal}
                    disabled={selectedForNomination.size === 0}
                    className={`h-9 px-4 rounded-xl flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide transition-colors ${
                      selectedForNomination.size > 0
                        ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <Send className="h-3.5 w-3.5" />
                    Nominate ({selectedForNomination.size})
                  </button>
                )}
              </div>

              {/* Filters */}
              {currentMapping && plannedTechnicians.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-xs"
                    />
                  </div>

                  {availableRoles.length > 0 && (
                    <select
                      value={selectedRoleFilter}
                      onChange={(e) => setSelectedRoleFilter(e.target.value)}
                      className="h-9 px-3 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs min-w-[140px]"
                    >
                      <option value="">All Roles</option>
                      {availableRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  )}

                  <select
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    className="h-9 px-3 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs min-w-[140px]"
                  >
                    <option value="">All Statuses</option>
                    <option value="authorized">Authorized</option>
                    <option value="pending">Pending</option>
                    <option value="no_auth">No Auth</option>
                  </select>

                  {(searchTerm || selectedRoleFilter || selectedStatusFilter) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedRoleFilter('');
                        setSelectedStatusFilter('');
                      }}
                      className="h-9 px-3 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors flex items-center gap-1"
                    >
                      <X className="h-3.5 w-3.5" />
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Technicians Table */}
            {loadingPlannedTechnicians ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2 text-slate-600 text-sm">Loading technicians...</span>
              </div>
            ) : (filteredPlannedTechnicians.length === 0 && selectedProjectTechnicians.length === 0) ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {(searchTerm || selectedRoleFilter || selectedStatusFilter) ? <Search className="h-7 w-7 text-slate-400" /> : <Users className="h-7 w-7 text-slate-400" />}
                </div>
                <h3 className="text-sm font-black text-slate-900 mb-2">
                  {(searchTerm || selectedRoleFilter || selectedStatusFilter) ? 'No results found' : 'No technicians'}
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  {(searchTerm || selectedRoleFilter || selectedStatusFilter)
                    ? 'Try adjusting your filters'
                    : currentMapping
                      ? 'No technicians planned on this Resource Board project.'
                      : 'Link a Resource Board project or assign technicians.'
                  }
                </p>
                {(searchTerm || selectedRoleFilter || selectedStatusFilter) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedRoleFilter('');
                      setSelectedStatusFilter('');
                    }}
                    className="text-[10px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-wide"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {currentMapping && techsEligibleForNomination.length > 0 && (
                        <th className="px-4 py-3 text-center w-12">
                          <button
                            onClick={toggleSelectAll}
                            className="p-1 hover:bg-slate-200 rounded-lg transition-colors"
                          >
                            {selectedForNomination.size === techsEligibleForNomination.length && techsEligibleForNomination.length > 0 ? (
                              <CheckSquare className="h-4 w-4 text-orange-500" />
                            ) : (
                              <Square className="h-4 w-4 text-slate-400" />
                            )}
                          </button>
                        </th>
                      )}
                      <th className="px-5 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">
                        Technician
                      </th>
                      <th
                        className="px-5 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900 select-none"
                        onClick={() => handleSort('roles')}
                      >
                        <div className="flex items-center gap-1">
                          Global Roles
                          <SortIcon column="roles" />
                        </div>
                      </th>
                      <th
                        className="px-5 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900 select-none"
                        onClick={() => handleSort('authorization')}
                      >
                        <div className="flex items-center gap-1">
                          OEM Authorization
                          <SortIcon column="authorization" />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">
                        Validity
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-5 py-3 text-right text-[10px] font-black text-slate-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {/* Planned technicians from Resource Board */}
                    {filteredPlannedTechnicians.map((tech) => {
                      const isAssigned = selectedProjectTechnicians.some(a => a.user === tech.id);
                      const canBeNominated = !tech.has_oem_authorization && !tech.has_pending_nomination;
                      const isSelected = selectedForNomination.has(tech.id);

                      return (
                        <tr key={`planned-${tech.id}`} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-orange-50' : ''}`}>
                          {currentMapping && techsEligibleForNomination.length > 0 && (
                            <td className="px-4 py-3 text-center">
                              {canBeNominated ? (
                                <button
                                  onClick={() => toggleTechnicianSelection(tech.id)}
                                  className="p-1 hover:bg-slate-200 rounded-lg transition-colors"
                                >
                                  {isSelected ? (
                                    <CheckSquare className="h-4 w-4 text-orange-500" />
                                  ) : (
                                    <Square className="h-4 w-4 text-slate-400" />
                                  )}
                                </button>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          )}
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              {tech.profile_picture ? (
                                <img
                                  src={getImageUrl(tech.profile_picture)}
                                  alt={tech.name}
                                  className="w-9 h-9 rounded-xl object-cover border-2 border-orange-100"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-sm">
                                  {tech.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                              )}
                              <div>
                                <span className="text-sm font-black text-slate-900">{tech.name}</span>
                                <p className="text-[10px] text-slate-500 font-medium">{tech.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            {tech.global_roles && tech.global_roles.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {tech.global_roles.map((role, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-block px-2 py-0.5 text-[9px] font-black rounded-md bg-slate-100 text-slate-700 uppercase"
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {tech.has_oem_authorization ? (
                              <span
                                className="inline-block px-2 py-0.5 text-[9px] font-black rounded-md text-white uppercase"
                                style={{ backgroundColor: tech.authorization_level_color || '#F97316' }}
                              >
                                {tech.authorization_level_code}
                              </span>
                            ) : tech.has_pending_nomination ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-amber-100 text-amber-700 uppercase">
                                <Clock className="h-3 w-3" />
                                Pending
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 text-[9px] font-black rounded-md bg-slate-100 text-slate-500 uppercase">
                                No Auth
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {tech.has_oem_authorization && tech.authorization_valid_from ? (
                              <div className="text-[10px] text-slate-600 font-medium">
                                <span>{formatDate(tech.authorization_valid_from)}</span>
                                <span className="text-slate-400 mx-1">→</span>
                                <span>{formatDate(tech.authorization_valid_until)}</span>
                              </div>
                            ) : tech.has_pending_nomination && tech.nomination_date_start ? (
                              <div className="text-[10px] text-amber-600 font-medium">
                                <span>{formatDate(tech.nomination_date_start)}</span>
                                <span className="text-amber-400 mx-1">→</span>
                                <span>{formatDate(tech.nomination_date_end)}</span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {isAssigned ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-emerald-100 text-emerald-600 uppercase">
                                <CheckCircle className="h-3 w-3" />
                                Assigned
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-violet-100 text-violet-600 uppercase">
                                <Calendar className="h-3 w-3" />
                                Planned
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-slate-300">-</span>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Assigned technicians not in planned list */}
                    {selectedProjectTechnicians
                      .filter(a => !filteredPlannedTechnicians.some(t => t.id === a.user))
                      .map((assignment) => (
                        <tr key={`assigned-${assignment.id}`} className="hover:bg-slate-50 transition-colors">
                          {currentMapping && techsEligibleForNomination.length > 0 && (
                            <td className="px-4 py-3 text-center">
                              <span className="text-slate-300">-</span>
                            </td>
                          )}
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              {assignment.profile_picture ? (
                                <img
                                  src={getImageUrl(assignment.profile_picture)}
                                  alt={assignment.user_name}
                                  className="w-9 h-9 rounded-xl object-cover border-2 border-emerald-100"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-black text-sm">
                                  {assignment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                              )}
                              <div>
                                <span className="text-sm font-black text-slate-900">{assignment.user_name}</span>
                                <p className="text-[10px] text-slate-500 font-medium">{assignment.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-[10px] text-slate-400">
                            -
                          </td>
                          <td className="px-5 py-3">
                            <span
                              className="inline-block px-2 py-0.5 text-[9px] font-black rounded-md text-white uppercase"
                              style={{ backgroundColor: assignment.authorization_level_color || '#F97316' }}
                            >
                              {assignment.authorization_level_code}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {assignment.authorization_valid_from ? (
                              <div className="text-[10px] text-slate-600 font-medium">
                                <span>{formatDate(assignment.authorization_valid_from)}</span>
                                <span className="text-slate-400 mx-1">→</span>
                                <span>{formatDate(assignment.authorization_valid_until)}</span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-emerald-100 text-emerald-600 uppercase">
                              <CheckCircle className="h-3 w-3" />
                              Assigned
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              onClick={() => handleUnassignFromProject(assignment)}
                              disabled={unassigningId === assignment.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-black text-red-600
                                       hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                              title="Unassign from project"
                            >
                              {unassigningId === assignment.id ? (
                                <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <UserMinus className="h-3 w-3" />
                              )}
                              Unassign
                            </button>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <AssignModal />
        <MappingModal />

        {/* Bulk Nomination Modal */}
        {showBulkNominationModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowBulkNominationModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Send className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Bulk Nominations</h3>
                    <p className="text-[10px] text-slate-500 font-medium">
                      {selectedForNomination.size} technician(s) selected
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBulkNominationModal(false)}
                  className="w-8 h-8 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>

              {/* Two Column Layout */}
              <div className="flex-1 flex overflow-hidden">
                {/* Left Column - Form */}
                <div className="w-1/3 border-r border-slate-200 p-5 space-y-4 overflow-y-auto">
                  <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">Nomination Details</h4>

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      Authorization Level *
                    </label>
                    <select
                      value={bulkNominationForm.authorization_level}
                      onChange={(e) => setBulkNominationForm(prev => ({ ...prev, authorization_level: e.target.value }))}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                    >
                      <option value="">Select level...</option>
                      {availableLevels.map(level => (
                        <option key={level.id} value={level.id}>
                          {level.code} - {level.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={bulkNominationForm.date_start}
                      onChange={(e) => setBulkNominationForm(prev => ({ ...prev, date_start: e.target.value }))}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={bulkNominationForm.date_end}
                      onChange={(e) => setBulkNominationForm(prev => ({ ...prev, date_end: e.target.value }))}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                    />
                  </div>

                  {bulkNominationForm.authorization_level && (
                    <div className="bg-slate-50 rounded-xl p-3">
                      <p className="text-[10px] font-black text-slate-600 mb-2 uppercase tracking-wide">Required Documents:</p>
                      {(() => {
                        const selectedLevel = availableLevels.find(l => l.id === Number(bulkNominationForm.authorization_level));
                        const requiredDocs = selectedLevel?.required_documents_data || [];
                        if (requiredDocs.length === 0) {
                          return <p className="text-[10px] text-slate-500 italic">No documents required</p>;
                        }
                        return (
                          <div className="space-y-1">
                            {requiredDocs.map(doc => (
                              <p key={doc.id} className="text-[10px] text-slate-700 font-medium">• {doc.name}</p>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-800 font-medium">
                      Nominations will be created separately and require individual approval.
                    </p>
                  </div>
                </div>

                {/* Right Column - Technicians Document Status */}
                <div className="w-2/3 p-5 overflow-y-auto bg-slate-50">
                  <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-wide mb-4">Document Status</h4>

                  {!bulkNominationForm.authorization_level ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-xs font-medium">Select an authorization level to see document requirements</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {plannedTechnicians
                        .filter(t => selectedForNomination.has(t.id))
                        .map(tech => {
                          const selectedLevel = availableLevels.find(l => l.id === Number(bulkNominationForm.authorization_level));
                          const requiredDocsData = selectedLevel?.required_documents_data || [];
                          const userQuals = (tech.qualifications || []).filter(q => q.status === 'valid');
                          const userQualIds = userQuals.map(q => q.qualification_type);

                          const okDocs = requiredDocsData.filter(doc => userQualIds.includes(doc.id));
                          const missingDocsData = requiredDocsData.filter(doc => !userQualIds.includes(doc.id));
                          const meetsRequirements = requiredDocsData.length === 0 || missingDocsData.length === 0;

                          return (
                            <div
                              key={tech.id}
                              className={`rounded-xl border p-4 ${meetsRequirements ? 'border-slate-200 bg-white' : 'border-amber-300 bg-amber-50'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  {tech.profile_picture ? (
                                    <img
                                      src={getImageUrl(tech.profile_picture)}
                                      alt={tech.name}
                                      className="w-9 h-9 rounded-xl object-cover border-2 border-orange-100"
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-black">
                                      {tech.name?.charAt(0)?.toUpperCase() || 'U'}
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-sm font-black text-slate-900">{tech.name}</p>
                                    <p className="text-[10px] text-slate-500 font-medium">
                                      {tech.global_roles?.join(', ') || 'No roles'}
                                    </p>
                                  </div>
                                </div>
                                {meetsRequirements ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] font-black rounded-lg bg-emerald-100 text-emerald-700 uppercase">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    Ready
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[9px] font-black rounded-lg bg-amber-100 text-amber-700 uppercase">
                                    <ShieldAlert className="h-3.5 w-3.5" />
                                    {missingDocsData.length} Missing
                                  </span>
                                )}
                              </div>

                              {requiredDocsData.length > 0 && (
                                <div className="ml-12 space-y-1.5">
                                  {okDocs.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {okDocs.map(doc => (
                                        <span key={doc.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-emerald-100 text-emerald-700 uppercase">
                                          <CheckCircle className="h-3 w-3" />
                                          {doc.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {missingDocsData.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {missingDocsData.map(doc => (
                                        <span key={doc.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black rounded-md bg-red-100 text-red-700 uppercase">
                                          <FileX className="h-3 w-3" />
                                          {doc.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {requiredDocsData.length === 0 && (
                                <p className="ml-12 text-[10px] text-slate-500 italic">No documents required</p>
                              )}
                            </div>
                          );
                        })
                      }
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-slate-100 flex justify-between bg-slate-50 flex-shrink-0">
                <button
                  onClick={() => setShowBulkNominationModal(false)}
                  className="h-10 px-4 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 font-black text-[10px] uppercase tracking-wide transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkNominationSubmit}
                  disabled={bulkNominationLoading || !bulkNominationForm.authorization_level || !bulkNominationForm.date_start || !bulkNominationForm.date_end}
                  className="h-10 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-black text-[10px] uppercase tracking-wide transition-colors shadow-lg shadow-orange-500/20"
                >
                  {bulkNominationLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Create {selectedForNomination.size} Nomination(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyProjects;
